import { Controller } from "@hotwired/stimulus"
import confetti from "canvas-confetti"

export default class extends Controller {
  static targets = ["modal", "populateButton", "continueButton", "spinner"]
  static values = {
    completeUrl: String,
    boardUrl: String,
    shown: Boolean
  }

  connect() {
    // Auto-show modal if outline is complete and hasn't been shown before
    if (this.isOutlineComplete() && !this.shownValue) {
      this.showModal()
    }
  }

  isOutlineComplete() {
    // Check if outline is complete (all 12 beats have 20+ words)
    const beatSections = document.querySelectorAll('[data-outline-target="beatSection"]')
    if (beatSections.length < 12) return false

    let completeCount = 0
    beatSections.forEach(section => {
      const textarea = section.querySelector('[data-outline-target="noteInput"]')
      if (textarea) {
        const wordCount = textarea.value.trim().split(/\s+/).filter(w => w).length
        if (wordCount >= 20) completeCount++
      }
    })

    return completeCount >= 12
  }

  // Admin trigger to simulate the modal
  simulate() {
    this.showModal()
  }

  showModal() {
    if (!this.hasModalTarget) return

    // Reset button states for testing
    if (this.hasPopulateButtonTarget) {
      this.populateButtonTarget.classList.remove("hidden")
      this.populateButtonTarget.disabled = false
      this.populateButtonTarget.querySelector('span').textContent = "Yes, prepare my board!"
    }
    if (this.hasContinueButtonTarget) {
      this.continueButtonTarget.classList.add("hidden")
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
    }

    // Show the modal
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"

    // Fire confetti celebration
    this.fireConfetti()
  }

  hideModal() {
    if (!this.hasModalTarget) return

    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  fireConfetti() {
    const colors = ['#f97316', '#fb923c', '#fbbf24', '#10b981', '#3b82f6']
    const duration = 3000
    const end = Date.now() + duration

    // Initial burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: colors
    })

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: colors
      })
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: colors
      })

      if (Date.now() < end) {
        requestAnimationFrame(frame)
      }
    }

    frame()
  }

  async populateBoard() {
    if (!this.hasPopulateButtonTarget) return

    // Show loading state
    this.populateButtonTarget.disabled = true
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
    }
    this.populateButtonTarget.querySelector('span').textContent = "Preparing board..."

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.completeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({ populate_board: "true" })
      })

      if (response.ok) {
        // Show success state
        this.populateButtonTarget.querySelector('span').textContent = "Board ready!"

        // Enable continue button
        if (this.hasContinueButtonTarget) {
          this.continueButtonTarget.classList.remove("hidden")
          this.populateButtonTarget.classList.add("hidden")
        }
      }
    } catch (error) {
      console.error("Failed to populate board:", error)
      this.populateButtonTarget.disabled = false
      this.populateButtonTarget.querySelector('span').textContent = "Try again"
    }
  }

  async skipPopulation() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(this.completeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({ populate_board: "false" })
      })
    } catch (error) {
      console.error("Failed to mark complete:", error)
    }

    this.goToBoard()
  }

  goToBoard() {
    window.location.href = this.boardUrlValue
  }

  continueEditing() {
    // Mark modal as shown without navigating
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.completeUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin",
      body: JSON.stringify({ populate_board: "false" })
    }).catch(() => {})

    this.hideModal()
  }
}
